import React, { useCallback, useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { routeAndSubmitKeywordSearch } from '../../util/searchSubmit';
import { getListingsById } from '../../ducks/marketplaceData.duck';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import StaticPage from '../PageBuilder/StaticPage';
import FooterContainer from '../FooterContainer/FooterContainer';

import Hero from './Hero/Hero';
import HomeListings from './HomeListings/HomeListings';
import { loadMoreNext, RESULT_PAGE_SIZE } from './HomePage.duck';

import css from './HomePage.module.css';

// ── Carried-over SEO/meta (Decision 5) ──────────────────────────────────────
// Copied from the prior hosted content/pages/landing-page.json `meta` block so
// SEO does not regress. Copy can be refined later.
const HOME_TITLE = 'Premier Horse - Buy, Sell & Advertise Premier Horses';
const HOME_DESCRIPTION =
  'Join Premier Horse, the top platform for buying, selling, and advertising Premier Horses in the United States. Secure online payments and a vibrant community await.';
const HOME_SCHEMA = {
  '@context': 'http://schema.org',
  '@type': 'WebPage',
  name: HOME_TITLE,
  description: HOME_DESCRIPTION,
};
// OG/Twitter sharing — image URLs are the prior hosted social-sharing asset
// variants (stable CDN URLs), so social previews are unchanged.
const HOME_SOCIAL_SHARING = {
  title: 'Premier Horse - Your Premier Horse Marketplace',
  description:
    'Discover, buy, sell, and advertise Premier Horses with ease on Premier Horse, the leading marketplace in the United States.',
  images1200: [
    {
      url:
        'https://sharetribe-assets.imgix.net/69c6b989-4f31-4d89-99cc-a6829ea9d60f/raw/f1/87969fa9f09d3f5b4813ea7a26f728eb60d5b1?auto=format&fit=clip&h=1200&w=1200&s=2ece4fac4460ed83bb58f24b96dfd5e2',
      width: 1200,
      height: 562,
    },
  ],
  images600: [
    {
      url:
        'https://sharetribe-assets.imgix.net/69c6b989-4f31-4d89-99cc-a6829ea9d60f/raw/f1/87969fa9f09d3f5b4813ea7a26f728eb60d5b1?auto=format&fit=clip&h=600&w=600&s=b4eb930c60566a47c984acda6a697dcf',
      width: 600,
      height: 281,
    },
  ],
};

export const HomePageComponent = props => {
  const {
    listings,
    pagination,
    searchInProgress,
    searchListingsError,
    currentUser,
    onLoadMore,
  } = props;

  const history = useHistory();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  // Scroll-dock: the homepage Topbar is transparent while the hero is in view,
  // then docks (solid + search revealed) once scrolled past it. An
  // IntersectionObserver on the hero region drives the `docked` flag — no scroll
  // listener. Default false (transparent) for SSR/first paint.
  const heroWrapRef = useRef(null);
  const [docked, setDocked] = useState(false);
  useEffect(() => {
    const node = heroWrapRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setDocked(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Hero search → reuse the exact route-at-submit + fail-open orchestration the
  // Topbar uses (Story 8.2 / AC9). ISOSearchBar handles the empty-query case.
  const handleSearchSubmit = useCallback(
    ({ keywords }) => {
      const rawQuery = (keywords || '').trim();
      const pushSearch = searchParams =>
        history.push(
          createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams)
        );
      if (!rawQuery) {
        pushSearch({});
        return undefined;
      }
      return routeAndSubmitKeywordSearch({
        rawQuery,
        currentSearchParams: {},
        currentUser,
        config,
        pushSearch,
      });
    },
    [history, routeConfiguration, currentUser, config]
  );

  const handleLoadMore = useCallback(() => onLoadMore(config), [onLoadMore, config]);

  return (
    <StaticPage
      title={HOME_TITLE}
      description={HOME_DESCRIPTION}
      schema={HOME_SCHEMA}
      socialSharing={HOME_SOCIAL_SHARING}
      className={css.root}
    >
      {/* The fixed homepage-variant Topbar overlays the hero; the observed
          heroWrap toggles the docked (solid + search) state on scroll. */}
      <TopbarContainer variant="homepage" docked={docked} currentPage="LandingPage" />
      <div className={css.heroWrap} ref={heroWrapRef}>
        <Hero onSearchSubmit={handleSearchSubmit} />
      </div>

      <main id="main-content" className={css.main}>
        <HomeListings
          listings={listings}
          pagination={pagination}
          loadInProgress={searchInProgress}
          error={searchListingsError}
          onLoadMore={handleLoadMore}
        />
      </main>

      <FooterContainer />
    </StaticPage>
  );
};

const mapStateToProps = state => {
  const {
    accumulatedResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
  } = state.HomePage;
  const { currentUser } = state.user;
  return {
    listings: getListingsById(state, accumulatedResultIds),
    pagination,
    searchInProgress,
    searchListingsError,
    currentUser,
  };
};

const mapDispatchToProps = dispatch => ({
  onLoadMore: config => dispatch(loadMoreNext(config)),
});

// Note: withRouter is not needed — HomePageComponent reads history via the
// useHistory hook. connect is sufficient.
const HomePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(HomePageComponent);

// Re-export for symmetry with other containers / convenience.
export { RESULT_PAGE_SIZE };

export default HomePage;
